import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import UpdateLayout from '../../layouts/UpdateLayout';
import AppScriptAPI from '../../services/appscriptAPI';
import { useUpdatePermissions } from '../../hooks/useUpdatePermissions';
import { useConsentModal } from '../../hooks/useConsentModal';
import AppToast from '../../components/AppToast';
import SelectWithOther from '../../components/SelectWithOther';

function UpdateDisability() {
  const { recordId } = useParams();
  const [searchParams] = useSearchParams();
  const sheetId = searchParams.get('sheetId');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const { canEdit } = useUpdatePermissions();
  const { confirmWithConsent, ConsentModalJSX } = useConsentModal();

  const [recordData, setRecordData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [legendData, setLegendData] = useState(null);

  const [formData, setFormData] = useState({
    uniqueId: '',
    name: '',
    // disabilityTypes: unique list of types that have at least one nature selected
    disabilityTypes: [],
    // defectiveNatures: { [disabilityType]: string[] } — multiple natures per type
    defectiveNatures: {},
    // otherNatures: { [disabilityType]: string } — free-text when 'Other' is checked
    otherNatures: {},
    causeOfDisability: '',
    otherCauseOfDisability: '',
    usesSignLanguage: '',
    usesBraille: '',
    medicalTreatment: '',
    otherMedicalTreatment: '',
    monthlyCost: '',
    otherMonthlyCost: '',
    hospitalType: '',
    otherHospitalType: '',
    periodOfDisability: '',
    usesAssistiveDevice: '',
    assistiveDeviceType: '',
    otherAssistiveDeviceType: '',
    equipmentIssuedBy: '',
    otherEquipmentIssuedBy: '',
    equipmentCondition: '',
    otherEquipmentCondition: '',
  });

  // Maps each disability type label to its legend key for nature options
  const disabilityDefectMapping = {
    'Visual Impairment': 'visualDefects',
    'Hearing Impairment Only': 'hearingDefects',
    'Speech Impairment Only': 'speechDefects',
    'Hearing and Speech Impairment': 'hearingSpeechDefects',
    'Cognitive Impairment': 'cognitiveDefects',
    'Intellectual Disability': 'intellectualDefects',
    'Cerebral Palsy': 'cerebralPalsyDefects',
    'Spinal Cord Injury': 'spinalCordDefects',
    'Brain Impairment': 'brainDefects',
    'Other Physical Impairment': 'otherPhysicalDefects',
    'Other Impairment': 'otherDefects',
  };

  useEffect(() => {
    if (!recordId || !sheetId) {
      setAlert({
        show: true,
        message: 'Missing required parameters. Please search for the record again.',
        type: 'danger',
      });
      setLoading(false);
      return;
    }
    loadRecordData();
  }, [recordId, sheetId]);

  const loadRecordData = async () => {
    try {
      const response = await AppScriptAPI.getDisabilityDetails(recordId, sheetId);

      if (!response.success) {
        setAlert({
          show: true,
          message: response.message || 'Failed to load disability details.',
          type: 'danger',
        });
        setLoading(false);
        return;
      }

      setRecordData(response.record);
      setLegendData(response.legend);

      const ro = (val, options) => (val && !options.includes(val) ? 'Other' : val);
      const rot = (val, options) => (val && !options.includes(val) ? val : '');

      const lg = response.legend || {};
      const causeOpts = lg.causeOfDisability || [];
      const medicalTreatmentOpts = lg.medicalTreatment || [];
      const monthlyCostOpts = lg.monthlyCost || [];
      const hospitalTypeOpts = lg.hospitalType || [];
      const assistiveDeviceTypeOpts = lg.assistiveDeviceTypes || [];
      const equipmentIssuedByOpts = lg.equipmentIssuedBy || [];
      const equipmentConditionOpts = lg.equipmentCondition || [];

      const typeStr = response.record.disabilityType || '';
      const natureStr = response.record.defectiveNature || '';
      const typeArr = typeStr.split(/\s*,\s*/).filter(Boolean);
      const natureArr = natureStr.split(/\s*,\s*/).filter(Boolean);

      const defectiveNaturesMap = {};
      const otherNaturesMap = {};
      typeArr.forEach((type, i) => {
        if (!natureArr[i]) return;
        if (!defectiveNaturesMap[type]) defectiveNaturesMap[type] = [];
        let nature = natureArr[i];
        if (nature.startsWith('Other:')) {
          otherNaturesMap[type] = nature.replace('Other:', '').trim();
          nature = 'Other';
        }
        if (!defectiveNaturesMap[type].includes(nature)) {
          defectiveNaturesMap[type].push(nature);
        }
      });
      // Unique types in original order
      const uniqueTypes = [...new Set(typeArr)].filter(t => defectiveNaturesMap[t]?.length);

      const rawCause = response.record.causeOfDisability || '';
      const rawMedical = response.record.medicalTreatment || '';
      const rawMonthlyCost = response.record.monthlyCost || '';
      const rawHospital = response.record.hospitalType || '';
      const rawDeviceType = response.record.assistiveDeviceType || '';
      const rawIssuedBy = response.record.equipmentIssuedBy || '';
      const rawCondition = response.record.equipmentCondition || '';

      const data = {
        uniqueId: response.record.uniqueId || '',
        name: response.record.name || '',
        disabilityTypes: uniqueTypes,
        defectiveNatures: defectiveNaturesMap,
        otherNatures: otherNaturesMap,
        causeOfDisability: ro(rawCause, causeOpts),
        otherCauseOfDisability: rot(rawCause, causeOpts),
        usesSignLanguage: response.record.usesSignLanguage || '',
        usesBraille: response.record.usesBraille || '',
        medicalTreatment: ro(rawMedical, medicalTreatmentOpts),
        otherMedicalTreatment: rot(rawMedical, medicalTreatmentOpts),
        monthlyCost: ro(rawMonthlyCost, monthlyCostOpts),
        otherMonthlyCost: rot(rawMonthlyCost, monthlyCostOpts),
        hospitalType: ro(rawHospital, hospitalTypeOpts),
        otherHospitalType: rot(rawHospital, hospitalTypeOpts),
        periodOfDisability: response.record.periodOfDisability || '',
        usesAssistiveDevice: response.record.usesAssistiveDevice || '',
        assistiveDeviceType: ro(rawDeviceType, assistiveDeviceTypeOpts),
        otherAssistiveDeviceType: rot(rawDeviceType, assistiveDeviceTypeOpts),
        equipmentIssuedBy: ro(rawIssuedBy, equipmentIssuedByOpts),
        otherEquipmentIssuedBy: rot(rawIssuedBy, equipmentIssuedByOpts),
        equipmentCondition: ro(rawCondition, equipmentConditionOpts),
        otherEquipmentCondition: rot(rawCondition, equipmentConditionOpts),
      };

      setFormData(data);
      setOriginalData(JSON.parse(JSON.stringify(data)));
      setLoading(false);
    } catch (error) {
      setAlert({ show: true, message: 'An error occurred: ' + error.message, type: 'danger' });
      setLoading(false);
    }
  };

  const handleNatureCheckbox = (type, nature) => {
    if (!canEdit) return;

    const currentNatures = formData.defectiveNatures[type] || [];
    const isChecked = currentNatures.includes(nature);

    const newNatures = { ...formData.defectiveNatures };
    const newOtherNatures = { ...formData.otherNatures };
    let newTypes = [...formData.disabilityTypes];

    if (isChecked) {
      const updated = currentNatures.filter(n => n !== nature);
      if (updated.length === 0) {
        delete newNatures[type];
        newTypes = newTypes.filter(t => t !== type);
      } else {
        newNatures[type] = updated;
      }
      if (nature === 'Other') delete newOtherNatures[type];
    } else {
      newNatures[type] = [...currentNatures, nature];
      if (!newTypes.includes(type)) newTypes = [...newTypes, type];
    }

    // Clear sign-language / braille answers if their trigger types were removed
    const updates = { defectiveNatures: newNatures, disabilityTypes: newTypes, otherNatures: newOtherNatures };
    const hearingSpeechTypes = ['Hearing Impairment Only', 'Speech Impairment Only', 'Hearing and Speech Impairment'];
    if (!hearingSpeechTypes.some(t => newTypes.includes(t))) {
      updates.usesSignLanguage = '';
    }
    if (!newTypes.includes('Visual Impairment')) {
      updates.usesBraille = '';
    }

    setFormData({ ...formData, ...updates });
  };

  const handleOtherNatureChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      otherNatures: { ...prev.otherNatures, [type]: value },
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updates = { [name]: value };

    if (name === 'usesAssistiveDevice') {
      if (value === 'No') {
        updates.assistiveDeviceType = '';
        updates.otherAssistiveDeviceType = '';
        updates.equipmentIssuedBy = '';
        updates.otherEquipmentIssuedBy = '';
        updates.equipmentCondition = '';
        updates.otherEquipmentCondition = '';
      } else if (value === 'Not using currently. But need an assistive device') {
        updates.equipmentIssuedBy = '';
        updates.otherEquipmentIssuedBy = '';
        updates.equipmentCondition = '';
        updates.otherEquipmentCondition = '';
      }
    }

    const clearMap = {
      causeOfDisability: 'otherCauseOfDisability',
      medicalTreatment: 'otherMedicalTreatment',
      monthlyCost: 'otherMonthlyCost',
      hospitalType: 'otherHospitalType',
      assistiveDeviceType: 'otherAssistiveDeviceType',
      equipmentIssuedBy: 'otherEquipmentIssuedBy',
      equipmentCondition: 'otherEquipmentCondition',
    };
    if (value !== 'Other' && clearMap[name]) updates[clearMap[name]] = '';

    setFormData({ ...formData, ...updates });
  };

  const getNatureOptions = (type) => {
    if (!type || !legendData) return [];
    const defectKey = disabilityDefectMapping[type];
    return defectKey && legendData[defectKey] ? legendData[defectKey] : [];
  };

  const shouldShowSignLanguage = () =>
    ['Hearing Impairment Only', 'Speech Impairment Only', 'Hearing and Speech Impairment']
      .some(t => formData.disabilityTypes.includes(t));

  const shouldShowBraille = () => formData.disabilityTypes.includes('Visual Impairment');

  const shouldShowDeviceType = () =>
    formData.usesAssistiveDevice === 'Yes' ||
    formData.usesAssistiveDevice === 'Not using currently. But need an assistive device';

  const shouldShowEquipmentDetails = () => formData.usesAssistiveDevice === 'Yes';

  const hasChanges = () => JSON.stringify(formData) !== JSON.stringify(originalData);

  const handleSave = async () => {
    if (formData.disabilityTypes.length === 0) {
      setAlert({
        show: true,
        message: 'Please select at least one disability nature.',
        type: 'warning',
      });
      return;
    }

    if (!hasChanges()) {
      setAlert({ show: true, message: 'No changes detected.', type: 'warning' });
      return;
    }

    if (!(await confirmWithConsent())) return;

    setSaving(true);

    try {
      const rf = (field, otherField) =>
        formData[field] === 'Other' && formData[otherField]
          ? formData[otherField]
          : formData[field];

      const updateData = {
        uniqueId: recordData.uniqueId,
        // Flatten: each type is repeated once per selected nature
        ...((() => {
          const pairs = formData.disabilityTypes.flatMap(t =>
            (formData.defectiveNatures[t] || []).map(n => ({
              type: t,
              nature: n === 'Other' && formData.otherNatures[t]
                ? `Other: ${formData.otherNatures[t]}`
                : n,
            }))
          );
          return {
            disabilityType: pairs.map(p => p.type).join(', '),
            defectiveNature: pairs.map(p => p.nature).join(', '),
          };
        })()),
        causeOfDisability: rf('causeOfDisability', 'otherCauseOfDisability'),
        usesSignLanguage: formData.usesSignLanguage,
        usesBraille: formData.usesBraille,
        medicalTreatment: rf('medicalTreatment', 'otherMedicalTreatment'),
        monthlyCost: rf('monthlyCost', 'otherMonthlyCost'),
        hospitalType: rf('hospitalType', 'otherHospitalType'),
        periodOfDisability: formData.periodOfDisability,
        usesAssistiveDevice: formData.usesAssistiveDevice,
        assistiveDeviceType: rf('assistiveDeviceType', 'otherAssistiveDeviceType'),
        equipmentIssuedBy: rf('equipmentIssuedBy', 'otherEquipmentIssuedBy'),
        equipmentCondition: rf('equipmentCondition', 'otherEquipmentCondition'),
        sheetId: recordData.sheetId,
        rowIndex: recordData.rowIndex,
      };

      const response = await AppScriptAPI.updateDisabilityDetails(updateData);

      if (response.success) {
        setAlert({ show: true, message: 'Disability details updated successfully.', type: 'success' });
        setOriginalData(JSON.parse(JSON.stringify(formData)));
      } else {
        setAlert({
          show: true,
          message: 'Failed to update: ' + (response.message || 'Unknown error'),
          type: 'danger',
        });
      }
    } catch (error) {
      setAlert({ show: true, message: 'An error occurred: ' + error.message, type: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => navigate(`/update/education/${recordId}?sheetId=${sheetId}`);
  const handlePrevious = () => navigate(`/update/housing/${recordId}?sheetId=${sheetId}`);

  if (loading) {
    return (
      <UpdateLayout currentStep={5} title="Update Disability Details" recordId={recordId} sheetId={sheetId}>
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading disability details...</p>
        </div>
      </UpdateLayout>
    );
  }

  if (!recordData) {
    return (
      <UpdateLayout currentStep={5} title="Update Disability Details" recordId={recordId} sheetId={sheetId}>
        <Alert variant="danger">
          <strong>Error!</strong>
          <br />
          {alert.message || 'Failed to load disability details.'}
        </Alert>
      </UpdateLayout>
    );
  }

  return (
    <UpdateLayout
      currentStep={5}
      title="Update Disability Details"
      recordId={recordId}
      sheetId={sheetId}
      onSave={handleSave}
      onNext={handleNext}
      onPrevious={handlePrevious}
      isSaving={saving}
      hasChanges={hasChanges()}
      canEdit={canEdit}
    >
      {ConsentModalJSX}
      <AppToast
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert(a => ({ ...a, show: false }))}
      />

      <Form>
        {/* ── Read-only record info ───────────────────────────────────────── */}
        <div className="section-header">Record Information (Read Only)</div>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Unique ID</Form.Label>
              <Form.Control type="text" value={formData.uniqueId} className="readonly-field" readOnly />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" value={formData.name} className="readonly-field" readOnly />
            </Form.Group>
          </Col>
        </Row>

        {/* ── Disability types & natures ──────────────────────────────────── */}
        <div className="section-header">Disability Information (Editable)</div>

        <Form.Group className="mb-4">
          <Form.Label>
            Disability Types <span className="text-danger">*</span>
          </Form.Label>
          <Form.Text className="d-block mb-3">
            Select all natures that apply. The disability type will be recorded automatically.
          </Form.Text>

          {/*
           * For each known disability type, show the type label as a sub-heading
           * and its nature options as checkboxes underneath.
           * Only types that have at least one nature option in the legend are rendered.
           */}
          <div className="disability-nature-groups">
            {(legendData?.disabilityTypes || []).map((type) => {
              const natures = getNatureOptions(type);
              if (natures.length === 0) return null;

              const selectedNatures = formData.defectiveNatures[type] || [];
              const typeIsActive = selectedNatures.length > 0;

              return (
                <div
                  key={type}
                  className={`disability-nature-group${typeIsActive ? ' active' : ''}${!canEdit ? ' disabled' : ''}`}
                >
                  {/* Type heading — shows a filled indicator when any nature is selected */}
                  <div className="disability-nature-group-header">
                    <span className={`disability-type-indicator${typeIsActive ? ' checked' : ''}`}>
                      {typeIsActive && <i className="bi bi-check-lg" />}
                    </span>
                    <span className="disability-type-label">{type}</span>
                  </div>

                  {/* Nature checkboxes */}
                  <div className="disability-nature-options">
                    {natures.map((nature, idx) => (
                      <Form.Check
                        key={idx}
                        type="checkbox"
                        id={`nature-${type}-${idx}`}
                        label={nature}
                        checked={selectedNatures.includes(nature)}
                        onChange={() => handleNatureCheckbox(type, nature)}
                        disabled={!canEdit}
                        className="disability-nature-check"
                      />
                    ))}
                    {selectedNatures.includes('Other') && (
                      <Form.Control
                        type="text"
                        className="mt-2"
                        placeholder="Please specify"
                        value={formData.otherNatures[type] || ''}
                        onChange={(e) => handleOtherNatureChange(type, e.target.value)}
                        disabled={!canEdit}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Form.Group>

        {/* ── Conditional: sign language ─────────────────────────────────── */}
        {shouldShowSignLanguage() && (
          <Form.Group className="mb-3 conditional-field">
            <Form.Label>Does the person use sign language?</Form.Label>
            <div>
              <Form.Check type="radio" label="Yes" name="usesSignLanguage" value="Yes"
                checked={formData.usesSignLanguage === 'Yes'} onChange={handleInputChange} disabled={!canEdit} />
              <Form.Check type="radio" label="No" name="usesSignLanguage" value="No"
                checked={formData.usesSignLanguage === 'No'} onChange={handleInputChange} disabled={!canEdit} />
            </div>
            <span className="info-badge">Shown for hearing / speech impairments</span>
          </Form.Group>
        )}

        {/* ── Conditional: braille ───────────────────────────────────────── */}
        {shouldShowBraille() && (
          <Form.Group className="mb-3 conditional-field">
            <Form.Label>Do you use Braille?</Form.Label>
            <div>
              <Form.Check type="radio" label="Yes" name="usesBraille" value="Yes"
                checked={formData.usesBraille === 'Yes'} onChange={handleInputChange} disabled={!canEdit} />
              <Form.Check type="radio" label="No" name="usesBraille" value="No"
                checked={formData.usesBraille === 'No'} onChange={handleInputChange} disabled={!canEdit} />
            </div>
            <span className="info-badge">Shown for visual impairment</span>
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Cause of Disability</Form.Label>
          <SelectWithOther
            name="causeOfDisability" value={formData.causeOfDisability}
            otherName="otherCauseOfDisability" otherValue={formData.otherCauseOfDisability}
            options={legendData?.causeOfDisability || []} onChange={handleInputChange}
            disabled={!canEdit} otherPlaceholder="Please specify cause of disability"
          />
        </Form.Group>

        {/* ── Medical ────────────────────────────────────────────────────── */}
        <div className="section-header">Medical Information</div>

        <Form.Group className="mb-3">
          <Form.Label>Medical Treatment for Disability</Form.Label>
          <SelectWithOther
            name="medicalTreatment" value={formData.medicalTreatment}
            otherName="otherMedicalTreatment" otherValue={formData.otherMedicalTreatment}
            options={legendData?.medicalTreatment || []} onChange={handleInputChange}
            disabled={!canEdit} otherPlaceholder="Please specify medical treatment"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Monthly Cost of Medical Follow Up</Form.Label>
          <SelectWithOther
            name="monthlyCost" value={formData.monthlyCost}
            otherName="otherMonthlyCost" otherValue={formData.otherMonthlyCost}
            options={legendData?.monthlyCost || []} onChange={handleInputChange}
            disabled={!canEdit} otherPlaceholder="Please specify monthly cost"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>From which kind of hospital do you get medical treatment?</Form.Label>
          <SelectWithOther
            name="hospitalType" value={formData.hospitalType}
            otherName="otherHospitalType" otherValue={formData.otherHospitalType}
            options={legendData?.hospitalType || []} onChange={handleInputChange}
            disabled={!canEdit} otherPlaceholder="Please specify hospital type"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Period of Disability (Years)</Form.Label>
          <Form.Control
            type="text" name="periodOfDisability" value={formData.periodOfDisability}
            onChange={handleInputChange} placeholder="e.g., 10" disabled={!canEdit}
          />
        </Form.Group>

        {/* ── Assistive devices ──────────────────────────────────────────── */}
        <div className="section-header">Assistive Devices</div>

        <Form.Group className="mb-3">
          <Form.Label>Are you using any assistive devices?</Form.Label>
          <div>
            <Form.Check type="radio" label="Yes" name="usesAssistiveDevice" value="Yes"
              checked={formData.usesAssistiveDevice === 'Yes'} onChange={handleInputChange} disabled={!canEdit} />
            <Form.Check type="radio" label="No" name="usesAssistiveDevice" value="No"
              checked={formData.usesAssistiveDevice === 'No'} onChange={handleInputChange} disabled={!canEdit} />
            <Form.Check
              type="radio" label="Not using currently, but need an assistive device"
              name="usesAssistiveDevice" value="Not using currently. But need an assistive device"
              checked={formData.usesAssistiveDevice === 'Not using currently. But need an assistive device'}
              onChange={handleInputChange} disabled={!canEdit}
            />
          </div>
        </Form.Group>

        {shouldShowDeviceType() && (
          <Form.Group className="mb-3 conditional-field">
            <Form.Label>Type of Equipment Used / Equipment Wanted to Use</Form.Label>
            <SelectWithOther
              name="assistiveDeviceType" value={formData.assistiveDeviceType}
              otherName="otherAssistiveDeviceType" otherValue={formData.otherAssistiveDeviceType}
              options={legendData?.assistiveDeviceTypes || []} onChange={handleInputChange}
              disabled={!canEdit} otherPlaceholder="Please specify assistive device type"
            />
          </Form.Group>
        )}

        {shouldShowEquipmentDetails() && (
          <>
            <Form.Group className="mb-3 conditional-field">
              <Form.Label>Equipment Issued By</Form.Label>
              <SelectWithOther
                name="equipmentIssuedBy" value={formData.equipmentIssuedBy}
                otherName="otherEquipmentIssuedBy" otherValue={formData.otherEquipmentIssuedBy}
                options={legendData?.equipmentIssuedBy || []} onChange={handleInputChange}
                disabled={!canEdit} otherPlaceholder="Please specify who issued the equipment"
              />
              <span className="info-badge">Only for currently used devices</span>
            </Form.Group>

            <Form.Group className="mb-3 conditional-field">
              <Form.Label>Condition of Equipment</Form.Label>
              <SelectWithOther
                name="equipmentCondition" value={formData.equipmentCondition}
                otherName="otherEquipmentCondition" otherValue={formData.otherEquipmentCondition}
                options={legendData?.equipmentCondition || []} onChange={handleInputChange}
                disabled={!canEdit} otherPlaceholder="Please specify equipment condition"
              />
              <span className="info-badge">Only for currently used devices</span>
            </Form.Group>
          </>
        )}
      </Form>
    </UpdateLayout>
  );
}

export default UpdateDisability;